/**
 * useCDPStream — React hook for connecting to the CDP screencast WebSocket.
 *
 * Manages socket.io connection to /screencast namespace,
 * handles frame streaming, element picking, and execution events.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';

const SCREENCAST_NAMESPACE = '/screencast';

export default function useCDPStream(apiBaseUrl, options = {}) {
  // engine: 'playwright' (default) uses a pure Playwright stealth context —
  //         fast launch + step mode; 'v4' launches Chromium with the v4
  //         learner extension (production-mirror, no step mode).
  const engine = options.engine === 'v4' ? 'v4' : 'playwright';
  const [connected, setConnected] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [currentFrame, setCurrentFrame] = useState(null);
  const [frameMetadata, setFrameMetadata] = useState(null);
  const [pickedElement, setPickedElement] = useState(null);
  const [currentUrl, setCurrentUrl] = useState('');
  const [error, setError] = useState(null);
  const [executionProgress, setExecutionProgress] = useState(null);
  const [executionResult, setExecutionResult] = useState(null);

  const socketRef = useRef(null);
  const frameCountRef = useRef(0);

  // Build socket URL — strip /api if present
  const socketUrl = (apiBaseUrl || '').replace(/\/api\/?$/, '') || '';

  useEffect(() => {
    // Get auth token
    const token = localStorage.getItem('token');

    const socket = io(`${socketUrl}${SCREENCAST_NAMESPACE}`, {
      transports: ['websocket'],
      auth: { token },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      setError(null);
    });

    socket.on('disconnect', () => {
      setConnected(false);
      setStreaming(false);
    });

    socket.on('connect_error', (err) => {
      setError(`Connection failed: ${err.message}`);
      setConnected(false);
    });

    // Screencast frames
    socket.on('screencast:frame', (data) => {
      setCurrentFrame(data.data); // base64 JPEG
      setFrameMetadata(data.metadata);
      frameCountRef.current += 1;
    });

    socket.on('screencast:started', (data) => {
      setStreaming(true);
      setCurrentUrl(data.url || '');
    });

    socket.on('screencast:stopped', () => {
      setStreaming(false);
      setCurrentFrame(null);
    });

    socket.on('screencast:error', (data) => {
      setError(data.message);
    });

    socket.on('screencast:elementPicked', (data) => {
      setPickedElement(data);
    });

    // Execution events
    socket.on('execution:progress', (data) => {
      setExecutionProgress(data);
    });

    socket.on('execution:completed', (data) => {
      setExecutionResult(data);
    });

    socket.on('execution:error', (data) => {
      setError(data.message || data.error);
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [socketUrl]);

  // Start screencast session
  const startScreencast = useCallback(
    (scriptId, credentialVaultId, url) => {
      const socket = socketRef.current;
      if (!socket || !connected) return;
      setError(null);
      setPickedElement(null);
      setExecutionProgress(null);
      setExecutionResult(null);
      frameCountRef.current = 0;
      socket.emit('screencast:start', {
        scriptId: scriptId ? String(scriptId) : undefined,
        credentialVaultId,
        url,
        // Backend reads this to decide between launchV4ForWatchMode and the
        // existing launchStealthBrowser path. Defaults to 'playwright' on
        // the backend if missing, so stale clients still work.
        extensionVariant: engine === 'v4' ? 'v4' : 'playwright',
      });
    },
    [connected, engine]
  );

  // Stop screencast session
  const stopScreencast = useCallback(() => {
    const socket = socketRef.current;
    if (!socket) return;
    socket.emit('screencast:stop', {});
    setStreaming(false);
    setCurrentFrame(null);
  }, []);

  // Click at coordinates in the CDP view
  const sendClick = useCallback(
    (x, y) => {
      const socket = socketRef.current;
      if (!socket || !streaming) return;
      socket.emit('screencast:click', { x, y });
    },
    [streaming]
  );

  // Type text into the focused element
  const sendType = useCallback(
    (text) => {
      const socket = socketRef.current;
      if (!socket || !streaming) return;
      socket.emit('screencast:type', { text });
    },
    [streaming]
  );

  // Navigate to a URL
  const sendNavigate = useCallback(
    (url) => {
      const socket = socketRef.current;
      if (!socket || !streaming) return;
      socket.emit('screencast:navigate', { url });
    },
    [streaming]
  );

  // Run all steps
  const runAll = useCallback(
    (credentialVaultId, variables) => {
      const socket = socketRef.current;
      if (!socket) return;
      // Don't gate on streaming — the caller handles timing
      setExecutionProgress(null);
      setExecutionResult(null);
      socket.emit('screencast:runAll', { credentialVaultId, variables });
    },
    []
  );

  // Run a single step by index
  const runStep = useCallback(
    (stepIndex, credentialVaultId, variables) => {
      const socket = socketRef.current;
      if (!socket) return;
      socket.emit('screencast:runStep', { stepIndex, credentialVaultId, variables });
    },
    []
  );

  return {
    // Connection state
    connected,
    streaming,
    error,

    // Frame data
    currentFrame,
    frameMetadata,
    frameCount: frameCountRef.current,

    // Interaction
    pickedElement,
    currentUrl,

    // Execution
    executionProgress,
    executionResult,

    // Actions
    startScreencast,
    stopScreencast,
    sendClick,
    sendType,
    sendNavigate,
    runAll,
    runStep,

    // Clear picked element
    clearPickedElement: () => setPickedElement(null),
  };
}
